import React, { useState, useContext, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
// Import Shared Components
import {
  PasswordRequirements,
  UsernameRequirements,
  Input,
  Checkbox,
} from 'shared-components';
// Import Shared Hooks
import { useUpdateStateObject } from 'shared-components/src/hooks/useUpdateStateObject';
// Import Shared utils
import { post } from 'shared-components/src/utils/http';
import { setToken } from 'shared-components/src/utils/auth';
import { ThemeContext } from 'styled-components';
// Import Shared Validations
import { verifyRegisterValidations } from 'shared-components/src/validations/cardholderValidations';
// Import Global Components
import InfoTooltip from '../../../components/InfoTooltip';
import InputGroup from '../../../layouts/InputGroup';
// Import Local Components
import AuthLayout from '../layouts/Auth';
import { AuthForm } from '../layouts/InnerAuth';
// Import Component Styles
import { FieldError, FormGroup, FormRow, PasswordFieldWrapper, RecaptchaWrapper } from '../styles';
// Import Utils
import { login } from '../../../utils/auth';
import { markPinSetupPending } from '../../../utils/pinSetup';
import {
  cardFieldError,
  validateUsername,
  validatePassword,
  checkForValidationErrors,
  validateConfirmPassword,
  errorMessage,
} from '../../../utils/validationHandlers';
import { digitsOnly, expandExpiry } from '../../../utils/cardFormatting';

import { authEn } from '../../../i18n/auth';
import TermsOfUseLink from './components/TermsOfUseLink';
import PwdEyeButton from './components/PwdEyeButton';
import CardFields from './components/CardFields';
import ReCAPTCHA from '../../../components/ReCaptcha';

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default () => {
  // Step tracking state (1 = Verify Card, 2 = Create Account)
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1 - Card verification state
  const initialVerifyInfo = {
    cardNumber: '',
    expiry: '',
    cvv: '',
  };

  const initialVerifyValidationErrors = {
    cardNumber: '',
    expiry: '',
    cvv: '',
  };

  // Step 2 - Registration state
  const initialRegisterInfo = {
    username: '',
    password: '',
    passwordConfirmation: '',
    email: '',
  };

  const initialRegisterValidationErrors = {
    username: '',
    password: '',
    passwordConfirmation: '',
    email: '',
  };

  // Verify response data (register_token and prefilled email)
  const [verifyResponse, setVerifyResponse] = useState({
    register_token: null,
    email: '',
    last_four_digits: '',
  });

  // Form state
  const [step1Error, setStep1Error] = useState(false);
  const [step2Error, setStep2Error] = useState(false);
  const [step1Loading, setStep1Loading] = useState(false);
  const [step2Loading, setStep2Loading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [passwordType, setPasswordType] = useState('password');
  const [confirmPasswordType, setConfirmPasswordType] = useState('password');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [termsOfUse, setTermsOfUse] = useState(false);
  const recaptchaRef = useRef(null);

  // Create intl instance
  const intl = useIntl();
  const navigate = useNavigate();
  const theme = useContext(ThemeContext);

  // Create state for Step 1 (card verification)
  const [verifyInfo, setVerifyInfo] = useUpdateStateObject(initialVerifyInfo);
  const [verifyValidationErrors, setVerifyValidationErrors] = useUpdateStateObject(
    initialVerifyValidationErrors
  );

  // Create state for Step 2 (registration)
  const [registerInfo, setRegisterInfo] = useUpdateStateObject(initialRegisterInfo);
  const [registerValidationErrors, setRegisterValidationErrors] = useUpdateStateObject(
    initialRegisterValidationErrors
  );

  // Email validation
  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return false;
    }
    return EMAIL_REGEX.test(email);
  };

  // Step 1 validation check
  const checkStep1Validation = () => {
    const errors = { ...verifyValidationErrors };
    let hasErrors = false;

    // Check card number, expiry and cvv
    ['cardNumber', 'expiry', 'cvv'].forEach(key => {
      const error = cardFieldError(key, verifyInfo);
      if (error) {
        errors[key] = error;
        hasErrors = true;
      }
    });

    // Update validation errors
    Object.keys(errors).forEach(key => {
      if (errors[key] !== verifyValidationErrors[key]) {
        setVerifyValidationErrors({ key, value: errors[key] });
      }
    });

    // Check if captcha is completed
    if (!captchaToken) {
      hasErrors = true;
    }

    return hasErrors;
  };

  // Step 2 validation check
  const checkStep2Validation = () => {
    const errors = { ...registerValidationErrors };
    let hasErrors = false;

    // Check username
    if (!verifyRegisterValidations.username(registerInfo.username)) {
      errors.username =
        registerInfo.username === ''
          ? 'errors-username-required'
          : 'errors-username-invalid';
      hasErrors = true;
    }

    // Check password
    if (!verifyRegisterValidations.password(registerInfo.password)) {
      errors.password =
        registerInfo.password === ''
          ? 'errors-password-required'
          : 'errors-password-invalid';
      hasErrors = true;
    }

    // Check password confirmation
    if (
      registerInfo.password !== registerInfo.passwordConfirmation ||
      registerInfo.passwordConfirmation === ''
    ) {
      errors.passwordConfirmation =
        registerInfo.passwordConfirmation === ''
          ? 'errors-password-confirmation-required'
          : 'errors-password-confirmation-invalid';
      hasErrors = true;
    }

    // Check email only if registration_success_email_enabled is true
    if (verifyResponse.registration_success_email_enabled) {
      if (!validateEmail(registerInfo.email)) {
        errors.email =
          registerInfo.email === ''
            ? 'errors-email-required'
            : 'errors-email-invalid';
        hasErrors = true;
      }
    }

    // Update validation errors
    Object.keys(errors).forEach(key => {
      if (errors[key] !== registerValidationErrors[key]) {
        setRegisterValidationErrors({ key, value: errors[key] });
      }
    });

    return hasErrors;
  };

  // Step 1 API error handler
  const handleStep1ApiError = error => {
    let fieldName = '';

    switch (error.code) {
    case 'invalid_card_number':
    case 'card_not_activated':
      fieldName = 'cardNumber';
      break;
    case 'failed_to_verify_card':
    case 'failed_to_verify_card_cvv':
      fieldName = 'cvv';
      break;
    case 'failed_to_verify_card_expiry_date':
    case 'failed_to_verify_card_expired':
      fieldName = 'expiry';
      break;
    case 'cardholder_already_registered':
      setStep1Error(`errors-${error.code}`);
      return;
    default:
      break;
    }

    if (fieldName) {
      setVerifyValidationErrors({
        key: fieldName,
        value: intl.messages[`errors-${error.code}`] ? `errors-${error.code}` : error.message,
      });
    } else {
      // Pass error code as i18n key for AuthLayout to display
      const errorKey = error.code ? `errors-${error.code}` : null;
      if (errorKey && intl.messages[errorKey]) {
        setStep1Error(errorKey);
      } else {
        // For unknown errors, use a generic error key or the message directly
        setStep1Error(error.message || 'errors-form-invalid');
      }
    }
  };

  // Captcha handlers
  const handleCaptchaChange = newToken => {
    setCaptchaToken(newToken);
    setStep1Error(false);
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
  };

  const handleCaptchaError = () => {
    setCaptchaToken(null);
    setStep1Error('errors-recaptcha_timeout');
    // Reset the captcha widget so user can try again
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const resetCaptcha = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    setCaptchaToken(null);
  };

  // Clear Step 2 form
  const clearStep2Form = () => {
    const fields = ['username', 'password', 'passwordConfirmation', 'email'];
    fields.forEach(field => {
      setRegisterInfo({ key: field, value: '' });
      setRegisterValidationErrors({ key: field, value: '' });
    });
  };

  // Register token missing/expired/invalid: return to Step 1 and ask the user
  // to verify their card again. The message is shown on Step 1 so it stays
  // visible until the user acts.
  const handleVerificationExpired = () => {
    setStep2Error(false);
    setSuccess(false);
    clearStep2Form();
    setVerifyResponse({
      register_token: null,
      email: '',
      last_four_digits: '',
      registration_success_email_enabled: false,
    });
    resetCaptcha();
    setCurrentStep(1);
    setStep1Error('errors-register_token_expired');
  };

  // Step 2 API error handler
  const handleStep2ApiError = error => {
    let fieldName = '';

    switch (error.code) {
    case 'username_not_available':
      fieldName = 'username';
      break;
    case 'invalid_password':
      fieldName = 'password';
      break;
    case 'invalid_email':
    case 'email_already_exists':
      fieldName = 'email';
      break;
    // The register token (and its pa_at cookie) is short-lived. When it is
    // missing, expired or invalid, CardholderJwtValidator rejects the request
    // with one of these codes. Send the user back to Step 1 with a persistent
    // message asking them to verify their card again.
    case 'missing_token':
    case 'token_expired':
    case 'invalid_token':
    case 'invalid_register_token':
    case 'register_token_expired':
      handleVerificationExpired();
      return;
    default:
      break;
    }

    if (fieldName) {
      setRegisterValidationErrors({
        key: fieldName,
        value: intl.messages[`errors-${error.code}`] ? `errors-${error.code}` : error.message,
      });
    } else {
      // Pass error code as i18n key for AuthLayout to display
      const errorKey = error.code ? `errors-${error.code}` : null;
      if (errorKey && intl.messages[errorKey]) {
        setStep2Error(errorKey);
      } else {
        // For unknown errors, use a generic error key or the message directly
        setStep2Error(error.message || 'errors-form-invalid');
      }
    }
  };

  // Handle back to Step 1
  const handleBackToStep1 = () => {
    setStep2Error(false);
    // Clear Step 2 validation errors
    const fields = ['username', 'password', 'passwordConfirmation', 'email'];
    fields.forEach(field => {
      setRegisterValidationErrors({ key: field, value: '' });
    });

    // Clear the verification token
    setVerifyResponse({
      register_token: null,
      email: '',
      last_four_digits: '',
    });

    // Go back to Step 1
    setCurrentStep(1);
  };

  // Step 1: Verify Card Handler
  const handleVerifyCard = async () => {
    if (checkStep1Validation()) return;

    // The field groups the number in fours and takes the expiry as MM/YY; send the digits
    // and a four-digit year.
    const cardNumber = digitsOnly(verifyInfo.cardNumber);
    const expiry = expandExpiry(verifyInfo.expiry);

    const verifyPayload = {
      card_number: cardNumber,
      expiry_month: expiry.slice(0, 2),
      expiry_year: expiry.slice(2),
      cvv: verifyInfo.cvv,
      recaptcha_token: captchaToken,
    };

    try {
      setStep1Loading(true);
      setStep1Error(false);

      const res = await post(
        '/api/v2/cardholder_portal/register/verify',
        verifyPayload
      );

      if (res.error) {
        const parsedError = {
          message: res.error.message || 'An unknown error occurred.',
          code: res.error.code || 'unknown_error',
        };
        throw parsedError;
      }

      // Store verification response data
      setVerifyResponse({
        register_token: res.data.register_token,
        email: res.data.email || '',
        last_four_digits: cardNumber.slice(-4),
        registration_success_email_enabled: res.data.registration_success_email_enabled || false,
      });

      // Prefill email if available from response
      if (res.data.email) {
        setRegisterInfo({ key: 'email', value: res.data.email });
      }

      // Reset captcha and proceed to Step 2
      resetCaptcha();
      setCurrentStep(2);
      setSuccess('register-verification-success');

    } catch (error) {
      handleStep1ApiError(error);
      resetCaptcha();
    } finally {
      setStep1Loading(false);
    }
  };

  // Step 2: Register Cardholder Handler
  const handleRegisterCardholder = async () => {
    if (checkStep2Validation()) return;

    const { username, password } = registerInfo;

    const registerPayload = {
      username: registerInfo.username,
      password: registerInfo.password,
      password_confirmation: registerInfo.passwordConfirmation,
      last_four_digits: verifyResponse.last_four_digits,
    };

    // Only include email if registration_success_email_enabled is true
    if (verifyResponse.registration_success_email_enabled) {
      registerPayload.email = registerInfo.email;
    }

    try {
      setStep2Loading(true);
      setStep2Error(false);
      setSuccess(false);

      // Set register_token in auth storage so it gets included in Authorization header
      setToken(verifyResponse.register_token, null);

      const res = await post(
        '/api/v2/cardholder_portal/register',
        registerPayload
      );

      if (res.error) {
        const parsedError = {
          message: res.error.message || 'An unknown error occurred.',
          code: res.error.code || 'unknown_error',
        };
        throw parsedError;
      }

      // Clear out form
      clearStep2Form();
      setSuccess('register-cardholder-success-alert');

      // Offer the PIN step once every auth factor below is done. Consumed by
      // whichever screen finishes the flow -- MFA setup, MFA verify, or the
      // auto-login. Registration resolves whether there is a step to show at
      // all, so an unset flag means the cardholder goes straight to the
      // dashboard rather than through a screen that would only bounce them.
      if (res.set_pin_offered) markPinSetupPending();

      // Check if registration returned MFA setup data (new flow)
      if (res.mfa_required && !res.mfa_registered && res.token) {

        // Set token from registration response
        setToken(res.token, res.exp);

        // Store MFA setup data for registration flow
        sessionStorage.setItem('mfa_setup_data', JSON.stringify({
          secret_code: res.secret_code,
          session: res.session,
          user_sub: res.user_sub,
          user_name: res.user_name
        }));

        // Redirect to MFA setup page
        window.location.pathname = '/mfa-setup';
        return;
      }

      // Fallback: Old flow - auto-login with stored credentials
      await login(username, password);
    } catch (error) {
      handleStep2ApiError(error);
    } finally {
      setStep2Loading(false);
    }
  };

  // Key up handlers
  const handleStep1KeyUp = async e => {
    if (e.keyCode === 13) {
      await handleVerifyCard();
    }
  };

  const handleStep2KeyUp = async e => {
    if (e.keyCode === 13) {
      await handleRegisterCardholder();
    }
  };

  const areAllAgreementsChecked = () => {
    if (
      theme.brand.registerTermsOfUseEn &&
      theme.brand.registerTermsOfUseFr &&
      !termsOfUse
    ) {
      return false;
    }
    return true;
  };

  const isStep1Valid = () => {
    if (!areAllAgreementsChecked()) {
      return false;
    }
    if (!captchaToken) {
      return false;
    }
    return true;
  };

  const isStep2Valid = () => {
    const baseValid = registerInfo.username &&
      registerInfo.password &&
      registerInfo.passwordConfirmation;

    // Only require email if registration_success_email_enabled is true
    if (verifyResponse.registration_success_email_enabled) {
      return baseValid && registerInfo.email;
    }
    return baseValid;
  };

  const usernameTooltip = (
    <InfoTooltip
      position="top"
      content={<UsernameRequirements messages={intl.messages} />}
    />
  );
  const passwordTooltip = (
    <InfoTooltip
      position="top"
      content={<PasswordRequirements messages={intl.messages} />}
    />
  );
  const registerLabel = (
    <FormattedMessage
      id="register-terms-of-use-label"
      description="Label for Terms of Use"
      defaultMessage={authEn['register-terms-of-use-label']}
      values={{
        link: <TermsOfUseLink />,
      }}
    />
  );

  const validatePasswordRules = (password = '') => {
    const rules = {
      minLength: password.length >= 12,
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSymbol: /[!@#$*]/.test(password),
    };

    const isValid = Object.values(rules).every(Boolean);

    return { rules, isValid };
  };

  const { rules } = validatePasswordRules(registerInfo.password);

  const handleCardFieldBlur = key => {
    setVerifyValidationErrors({ key, value: cardFieldError(key, verifyInfo) });
  };

  // Render Step 1 - Card Verification Form
  const renderStep1Form = () => (
    <AuthForm
      onSubmit={e => {
        e.preventDefault();
        checkForValidationErrors(verifyValidationErrors, setStep1Error);
        handleVerifyCard();
      }}
      onKeyUp={handleStep1KeyUp}
      loading={step1Loading}
      submitButtonDisabled={!isStep1Valid()}
      // Nothing on this form resolves an already-registered card, so offer the way to login.
      secondaryButtonFunc={
        step1Error === 'errors-cardholder_already_registered' ? () => navigate('/login') : null
      }
      secondaryButtonText={intl.messages['register-go-to-login-button']}
      secondaryButtonOutlined
    >
      <CardFields
        values={verifyInfo}
        errors={verifyValidationErrors}
        onChange={(key, value) => setVerifyInfo({ key, value })}
        onBlur={handleCardFieldBlur}
      />
      <FormGroup>
        <RecaptchaWrapper>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
            onExpired={handleCaptchaExpire}
            onErrored={handleCaptchaError}
          />
        </RecaptchaWrapper>
      </FormGroup>
      {theme.brand.registerTermsOfUseEn && theme.brand.registerTermsOfUseFr && (
        <FormRow>
          <Checkbox
            label={registerLabel}
            labelFor="registerTermsOfUseInput"
            id="register-terms-of-use-input"
            value={termsOfUse}
            onChange={e => setTermsOfUse(e.target.checked)}
          />
        </FormRow>
      )}
    </AuthForm>
  );

  // Render Step 2 - Registration Form
  const renderStep2Form = () => (
    <AuthForm
      onSubmit={e => {
        e.preventDefault();
        checkForValidationErrors(registerValidationErrors, setStep2Error);
        handleRegisterCardholder();
      }}
      onKeyUp={handleStep2KeyUp}
      loading={step2Loading}
      submitButtonDisabled={!isStep2Valid()}
      submitButtonText={intl.messages['register-create-account-button']}
      secondaryButtonFunc={handleBackToStep1}
      secondaryButtonText={intl.messages['register-back-button']}
    >
      <FormGroup>
        {verifyResponse.registration_success_email_enabled && (
          <InputGroup
            label={intl.messages['auth-email-label']}
            labelFor="email"
            page="auth"
          >
            <Input
              className={`${registerValidationErrors.email ? 'is-invalid' : ''}`}
              value={registerInfo.email}
              type="email"
              onBlur={e => {
                if (e.target.value.trim()) {
                  if (!validateEmail(e.target.value.trim())) {
                    setRegisterValidationErrors({
                      key: 'email',
                      value: 'errors-email-invalid',
                    });
                  } else {
                    setRegisterValidationErrors({
                      key: 'email',
                      value: '',
                    });
                  }
                } else {
                  setRegisterValidationErrors({
                    key: 'email',
                    value: 'errors-email-required',
                  });
                }
              }}
              onChange={e => {
                setRegisterInfo({
                  key: 'email',
                  value: e.target.value.trim(),
                });
              }}
              placeholder={intl.messages['register-email-placeholder']}
            />
            {registerValidationErrors.email && (
              <FieldError className="invalid-feedback">
                {errorMessage(intl, registerValidationErrors.email)}
              </FieldError>
            )}
          </InputGroup>
        )}
        <InputGroup
          tooltip={usernameTooltip}
          label={intl.messages['auth-username-label']}
          labelFor="username"
          page="auth"
        >
          <Input
            className={`${registerValidationErrors.username ? 'is-invalid' : ''}`}
            value={registerInfo.username}
            error={registerValidationErrors.username}
            onBlur={e => {
              if (e.target.value.trim()) {
                validateUsername(setRegisterValidationErrors, registerInfo);
              } else {
                setRegisterValidationErrors({
                  key: 'username',
                  value: 'errors-username-required',
                });
              }
            }}
            onChange={e => {
              setRegisterInfo({
                key: 'username',
                value: e.target.value.trim(),
              });
            }}
            minLength={3}
            maxLength={30}
            placeholder={intl.messages['register-username-placeholder']}
          />
          {registerValidationErrors.username && (
            <FieldError className="invalid-feedback">
              {errorMessage(intl, registerValidationErrors.username)}
            </FieldError>
          )}
        </InputGroup>
        <InputGroup
          tooltip={passwordTooltip}
          label={intl.messages['auth-password-label']}
          labelFor="password"
          page="auth"
        >
          <PasswordFieldWrapper>
            <Input
              className={`${registerValidationErrors.password ? 'is-invalid' : ''}`}
              value={registerInfo.password}
              error={registerValidationErrors.password}
              onBlur={e => {
                if (e.target.value.trim()) {
                  validatePassword(setRegisterValidationErrors, registerInfo);
                } else {
                  setRegisterValidationErrors({
                    key: 'password',
                    value: 'errors-password-required',
                  });
                }
              }}
              onChange={e => {
                setRegisterInfo({
                  key: 'password',
                  value: e.target.value.trim(),
                });
              }}
              type={passwordType}
              autoComplete="new-password"
              minLength={12}
              placeholder={intl.messages['register-password-placeholder']}
            />
            <PwdEyeButton type={passwordType} settype={setPasswordType} />
          </PasswordFieldWrapper>
          {registerValidationErrors.password && (
            <FieldError className="invalid-feedback">
              {errorMessage(intl, registerValidationErrors.password)}
              {registerInfo.password && (
                <div>
                  <p className={rules.minLength ? 'valid' : 'invalid'}>
                    • {intl.messages['errors-password-length']}
                  </p>
                  <p className={rules.hasUppercase ? 'valid' : 'invalid'}>
                    • {intl.messages['errors-password-case']}
                  </p>
                  <p className={rules.hasNumber ? 'valid' : 'invalid'}>
                    • {intl.messages['errors-password-number']}
                  </p>
                  <p className={rules.hasSymbol ? 'valid' : 'invalid'}>
                    • {intl.messages['errors-password-symbol']}
                  </p>
                </div>
              )}
            </FieldError>
          )}
        </InputGroup>
        <InputGroup
          label={intl.messages['auth-password-confirmation-label']}
          labelFor="password-confirmation"
          page="auth"
        >
          <PasswordFieldWrapper>
            <Input
              className={`${
                registerValidationErrors.passwordConfirmation ? 'is-invalid' : ''
              }`}
              value={registerInfo.passwordConfirmation}
              error={registerValidationErrors.passwordConfirmation}
              onBlur={e => {
                if (e.target.value.trim()) {
                  validateConfirmPassword(setRegisterValidationErrors, registerInfo);
                } else {
                  setRegisterValidationErrors({
                    key: 'passwordConfirmation',
                    value: 'errors-password-confirmation-required',
                  });
                }
              }}
              onChange={e => {
                setRegisterInfo({
                  key: 'passwordConfirmation',
                  value: e.target.value.trim(),
                });
              }}
              type={confirmPasswordType}
              autoComplete="new-password"
              minLength={12}
              placeholder={intl.messages['register-password-confirmation-placeholder']}
            />
            <PwdEyeButton type={confirmPasswordType} settype={setConfirmPasswordType} />
          </PasswordFieldWrapper>
          {registerValidationErrors.passwordConfirmation && (
            <FieldError className="invalid-feedback">
              {errorMessage(intl, registerValidationErrors.passwordConfirmation)}
            </FieldError>
          )}
        </InputGroup>
      </FormGroup>
    </AuthForm>
  );

  return (
    <AuthLayout
      page="register"
      heading={intl.messages['register-heading']}
      subHeaderOne={intl.messages['register-subheader1-text']}
      // The activation link only helps before the card is verified.
      subHeaderTwo={currentStep === 1 ? intl.messages['register-subheader2-text'] : null}
      error={currentStep === 1 ? step1Error : step2Error}
      success={success}
    >
      {currentStep === 1 ? renderStep1Form() : renderStep2Form()}
    </AuthLayout>
  );
};
